import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Assignment as AssignIcon,
  Undo as ReturnIcon,
  SwapHoriz as TransferIcon,
  CheckCircle as CheckoutIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import equipmentService from '../services/equipment.service';
import api from '../services/api.service';
import { getStatusDisplay } from '../constants/equipmentStatus';
import AssignInventory from './AssignInventory';
import ReturnInventory from './ReturnInventory';
import TransferInventory from './TransferInventory';

const StatusHistoryDialog = ({ open, onClose, equipment, onEquipmentUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [currentEquipment, setCurrentEquipment] = useState(equipment);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Checkout form state
  const [checkoutTechName, setCheckoutTechName] = useState('');
  const [checkoutProcessing, setCheckoutProcessing] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(false);

  // Action dialog states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);

  useEffect(() => {
    if (open && equipment?.id) {
      setCurrentEquipment(equipment);
      loadStatusHistory();
    }
  }, [open, equipment]);

  // Load technicians when dialog opens
  useEffect(() => {
    const loadTechnicians = async () => {
      if (!open) return;

      setLoadingTechs(true);
      try {
        const response = await api.get('/auth/technicians');
        const techsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setTechnicians(techsData);
      } catch (err) {
        console.error('Error loading technicians:', err);
      } finally {
        setLoadingTechs(false);
      }
    };
    loadTechnicians();
  }, [open]);

  const loadStatusHistory = async () => {
    try {
      setLoading(true);
      console.log('StatusHistoryDialog: Loading history for equipment:', equipment);
      console.log('StatusHistoryDialog: Using equipment ID:', equipment.id);
      console.log('StatusHistoryDialog: Available equipment fields:', Object.keys(equipment));

      const history = await equipmentService.getEquipmentStatusHistory(equipment.id);
      console.log('StatusHistoryDialog: Received history:', history);
      setStatusHistory(history || []);
    } catch (err) {
      console.error('Error loading status history:', err);
      setStatusHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const renderStatusChip = (status) => {
    const display = getStatusDisplay(status);
    return (
      <Chip
        label={display.label}
        size="small"
        sx={{
          backgroundColor: display.color,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  };

  // Handle action completion - reload status history AND refresh equipment data
  const handleActionComplete = async () => {
    await loadStatusHistory();

    // Refresh equipment data to get updated status
    try {
      const response = await equipmentService.getEquipmentById(equipment.id);

      // Extract the actual equipment data from the response
      // API returns {data: {equipment object}} or {success: true, data: {equipment object}}
      const refreshedEquipment = response?.data || response;

      if (refreshedEquipment && refreshedEquipment.id) {
        setCurrentEquipment(refreshedEquipment);

        // Notify parent component if callback provided
        if (onEquipmentUpdate) {
          onEquipmentUpdate(refreshedEquipment);
        }

        // Show success message
        setSuccessMessage('Equipment status updated successfully');
        setShowSuccessMessage(true);

        // Dispatch global event for other components to refresh
        const eventDetail = {
          action: 'status_changed',
          equipmentIds: [refreshedEquipment.id],
          newStatus: refreshedEquipment.status,
          timestamp: new Date().toISOString()
        };
        console.log('📢 StatusHistoryDialog dispatching equipmentStatusChanged event:', eventDetail);
        window.dispatchEvent(new CustomEvent('equipmentStatusChanged', { detail: eventDetail }));

        // Close action dialogs
        setShowReturnDialog(false);
        setShowAssignDialog(false);
        setShowTransferDialog(false);
        setShowCheckoutDialog(false);
      }
    } catch (err) {
      console.error('Error refreshing equipment data:', err);
    }
  };

  // Action handlers - ensure only one dialog shows at a time
  const handleAssign = () => {
    setShowReturnDialog(false);
    setShowTransferDialog(false);
    setShowCheckoutDialog(false);
    setShowAssignDialog(true);
  };

  const handleReturn = () => {
    setShowAssignDialog(false);
    setShowTransferDialog(false);
    setShowCheckoutDialog(false);
    setShowReturnDialog(true);
  };

  const handleTransfer = () => {
    setShowAssignDialog(false);
    setShowReturnDialog(false);
    setShowCheckoutDialog(false);
    setShowTransferDialog(true);
  };

  const handleCheckout = () => {
    setShowAssignDialog(false);
    setShowReturnDialog(false);
    setShowTransferDialog(false);
    setShowCheckoutDialog(true);

    // Scroll to checkout form after it renders
    setTimeout(() => {
      const checkoutForm = document.querySelector('[data-checkout-form]');
      if (checkoutForm) {
        checkoutForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // Handle checkout submission
  const handleCheckoutSubmit = async () => {
    if (!checkoutTechName.trim()) {
      enqueueSnackbar('Please enter technician name', { variant: 'warning' });
      return;
    }

    try {
      setCheckoutProcessing(true);

      // Call batch-checkout endpoint
      const response = await api.post('/equipment-workflow/batch-checkout', {
        items: [{ equipment_id: currentEquipment.equipment_id }],
        assignedTo: checkoutTechName.trim(),
        userId: user?.id || 'system'
      });

      if (response.data?.success) {
        setCheckoutTechName('');
        setShowCheckoutDialog(false);
        await handleActionComplete();
      } else {
        enqueueSnackbar('Failed to checkout equipment', { variant: 'error' });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to checkout equipment', { variant: 'error' });
    } finally {
      setCheckoutProcessing(false);
    }
  };

  // Get available actions based on equipment status
  const getAvailableActions = (equip) => {
    if (!equip) return [];

    const actions = [];

    if ((equip.status === 'available' || equip.status === 'returned') && !equip.current_job_id) {
      // For available/returned stock equipment - can checkout directly OR assign to job
      actions.push({ type: 'checkout', label: 'Checkout', icon: CheckoutIcon, handler: handleCheckout });
      actions.push({ type: 'assign', label: 'Assign to Job', icon: AssignIcon, handler: handleAssign });
    } else if (equip.status === 'assigned' || equip.status === 'checked_out' || equip.current_job_id) {
      // For assigned equipment - return and transfer options
      actions.push({ type: 'return', label: 'Return', icon: ReturnIcon, handler: handleReturn });
      actions.push({ type: 'transfer', label: 'Transfer', icon: TransferIcon, handler: handleTransfer });
    }

    return actions;
  };

  if (!currentEquipment) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '400px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6">Equipment History</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentEquipment.equipment_id || currentEquipment.id}
              {currentEquipment.description && ` - ${currentEquipment.description}`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {currentEquipment.serial_number && (
                <Typography variant="caption" color="text.secondary" sx={{
                  bgcolor: 'grey.100',
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontWeight: 'medium'
                }}>
                  SN: {currentEquipment.serial_number}
                </Typography>
              )}
              {currentEquipment.manufacturer && (
                <Typography variant="caption" color="text.secondary">
                  {currentEquipment.manufacturer}
                </Typography>
              )}
              {currentEquipment.model && (
                <Typography variant="caption" color="text.secondary">
                  {currentEquipment.model}
                </Typography>
              )}
            </Box>

            {/* Job assignment and location info - prominent when assigned/installed */}
            {(currentEquipment.current_job_id || currentEquipment.current_site_id) && (
              <Box sx={{
                mt: 1,
                p: 1,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                {currentEquipment.current_job_id && (
                  <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                    📋 Assigned to Job {currentEquipment.current_job_id}
                  </Typography>
                )}
                {currentEquipment.current_site_id && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    📍 Location: Site {currentEquipment.current_site_id}
                  </Typography>
                )}
                {(currentEquipment.warehouse_id || currentEquipment.rack_id) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    🏪 Storage: {currentEquipment.warehouse_name || 'Warehouse'}{currentEquipment.rack_name && ` - ${currentEquipment.rack_name}`}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {currentEquipment.status && renderStatusChip(currentEquipment.status)}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : statusHistory.length > 0 ? (
          <Timeline position={isMobile ? 'right' : 'alternate'}>
            {statusHistory.map((entry, index) => {
              const display = getStatusDisplay(entry.status);
              const IconComponent = display.icon;
              return (
                <TimelineItem key={index}>
                  {!isMobile && (
                    <TimelineOppositeContent color="text.secondary">
                      {entry.timestamp && !isNaN(new Date(entry.timestamp)) 
                        ? format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')
                        : 'Date unavailable'}
                    </TimelineOppositeContent>
                  )}
                  <TimelineSeparator>
                    <TimelineDot sx={{ backgroundColor: display.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconComponent size={16} style={{ color: 'white' }} />
                    </TimelineDot>
                    {index < statusHistory.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="h6">{display.label}</Typography>
                    {isMobile && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(entry.timestamp || entry.created_at) && !isNaN(new Date(entry.timestamp || entry.created_at))
                          ? format(new Date(entry.timestamp || entry.created_at), 'MMM d, yyyy h:mm a')
                          : 'Date unavailable'}
                      </Typography>
                    )}
                    
                    {/* Special details for RECEIVED status */}
                    {(entry.newStatus === 'RECEIVED' || entry.status === 'RECEIVED') && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Equipment received into inventory
                        </Typography>
                        {entry.quantity && (
                          <Typography variant="body2" color="text.secondary">
                            Quantity: {entry.quantity}
                          </Typography>
                        )}
                        {(entry.warehouse || equipment.warehouse) && (
                          <Typography variant="body2" color="text.secondary">
                            Warehouse: {entry.warehouse || equipment.warehouse}
                            {(entry.zone || equipment.zone) && ` - Zone: ${entry.zone || equipment.zone}`}
                          </Typography>
                        )}
                        {entry.notes && entry.notes !== 'Equipment received into inventory' && (
                          <Typography variant="body2" color="text.secondary">
                            Notes: {entry.notes}
                          </Typography>
                        )}
                        {entry.isTransaction && (
                          <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                            Transaction Record
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Special details for RETURNED status */}
                    {(entry.newStatus === 'RETURNED' || entry.status === 'RETURNED' || entry.activityType === 'returned') && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Equipment returned to warehouse
                        </Typography>
                        {entry.fromJobId && (
                          <Typography variant="body2" color="text.secondary">
                            From Job: {entry.fromJobName || entry.fromJobId}
                          </Typography>
                        )}
                        {(entry.warehouseId || entry.warehouse) && (
                          <Typography variant="body2" color="text.secondary">
                            To: {entry.warehouseName || entry.warehouse}
                            {(entry.rackName || entry.rack) && ` - ${entry.rackName || entry.rack}`}
                            {(entry.bayName || entry.bay) && ` - ${entry.bayName || entry.bay}`}
                          </Typography>
                        )}
                        {entry.notes && (
                          <Typography variant="body2" color="text.secondary">
                            {entry.notes}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Special details for TRANSFERRED status */}
                    {(entry.newStatus === 'TRANSFERRED' || entry.status === 'TRANSFERRED' || entry.activityType === 'transferred') && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Equipment transferred
                        </Typography>
                        {entry.fromJobId && (
                          <Typography variant="body2" color="text.secondary">
                            From: {entry.fromJobName || `Job ${entry.fromJobId}`}
                          </Typography>
                        )}
                        {entry.toJobId && (
                          <Typography variant="body2" color="text.secondary">
                            To: {entry.toJobName || `Job ${entry.toJobId}`}
                          </Typography>
                        )}
                        {entry.toSiteId && (
                          <Typography variant="body2" color="text.secondary">
                            Site: {entry.toSiteName || entry.toSiteId}
                          </Typography>
                        )}
                        {entry.notes && (
                          <Typography variant="body2" color="text.secondary">
                            {entry.notes}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Job and Site information */}
                    {(entry.jobId || entry.siteId) && (
                      <Box sx={{ mt: 1 }}>
                        {entry.jobId && (
                          <Typography variant="body2" color="text.secondary">
                            Job: {entry.job?.name || entry.jobId}
                          </Typography>
                        )}
                        {entry.siteId && (
                          <Typography variant="body2" color="text.secondary">
                            Site: {entry.site?.name || entry.siteId}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {entry.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Notes: {entry.notes}
                      </Typography>
                    )}
                    
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      By: {entry.changedBy || entry.user || 'System'}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        ) : (
          <Alert severity="info">No status history available for this equipment</Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3, justifyContent: 'center' }}>
          {getAvailableActions(currentEquipment).map((action) => (
            <Button
              key={action.type}
              variant="outlined"
              size="small"
              startIcon={<action.icon />}
              onClick={action.handler}
            >
              {action.label}
            </Button>
          ))}
        </Box>

        {/* Inline Return Form */}
        {currentEquipment && showReturnDialog && (
          <Box sx={{ mt: 3 }}>
            <ReturnInventory
              open={showReturnDialog}
              onClose={() => setShowReturnDialog(false)}
              selectedItems={[currentEquipment]}
              onReturnComplete={handleActionComplete}
            />
          </Box>
        )}

        {/* Inline Transfer Form */}
        {currentEquipment && showTransferDialog && (
          <Box sx={{ mt: 3 }}>
            <TransferInventory
              selectedEquipment={[currentEquipment]}
              hideBarcode={true}
              mode="specific-equipment"
              onTransferComplete={() => {
                setShowTransferDialog(false);
                handleActionComplete();
              }}
              onClose={() => setShowTransferDialog(false)}
            />
          </Box>
        )}

        {/* Inline Checkout Form */}
        {currentEquipment && showCheckoutDialog && (
          <Box
            data-checkout-form
            sx={{
              mt: 3,
              p: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Checkout Equipment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select technician to checkout {currentEquipment.equipment_id}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="checkout-tech-label">Select Technician</InputLabel>
              <Select
                labelId="checkout-tech-label"
                value={checkoutTechName}
                onChange={(e) => setCheckoutTechName(e.target.value)}
                label="Select Technician"
                disabled={checkoutProcessing || loadingTechs}
              >
                {loadingTechs ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading technicians...
                  </MenuItem>
                ) : technicians.length === 0 ? (
                  <MenuItem disabled>No technicians available</MenuItem>
                ) : (
                  technicians.map((tech) => (
                    <MenuItem key={tech.id} value={tech.name}>
                      {tech.name} ({tech.email})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setShowCheckoutDialog(false)}
                disabled={checkoutProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCheckoutSubmit}
                disabled={checkoutProcessing || !checkoutTechName.trim()}
                startIcon={checkoutProcessing ? <CircularProgress size={16} /> : null}
              >
                {checkoutProcessing ? 'Checking out...' : 'Checkout'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Assign Dialog - still modal */}
      {currentEquipment && showAssignDialog && (
        <AssignInventory
          open={showAssignDialog}
          onClose={() => setShowAssignDialog(false)}
          selectedEquipment={[currentEquipment]}
          hideBarcode={true}
          mode="specific-equipment"
          onAssignComplete={handleActionComplete}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default StatusHistoryDialog;